export interface TherapeuticPresenceUser {
  firstName: string;
  email: string;
  id: string;
}

export interface TherapeuticPresenceProps {
  user: TherapeuticPresenceUser;
  onComplete?: (resultId: string) => void;
  apiEndpoint?: string;
  historyUrl?: string;
  source?: string;
}

export interface Question {
  q: string;
  cat: 'immersion' | 'attunement' | 'emotional' | 'focus';
  reverse: boolean;
}

export interface CategoryMeta {
  emoji: string;
  name: string;
  desc: string;
}

export interface Scores {
  immersion: number;
  attunement: number;
  emotional: number;
  focus: number;
}

// Question data
export const questions: Question[] = [
  // Immersion & Flow (5 questions)
  { q: 'I was aware of my own internal flow of experiencing.', cat: 'immersion', reverse: false },
  { q: 'The interaction between my client and I felt flowing and rhythmic.', cat: 'immersion', reverse: false },
  { q: "There were moments when I was so immersed in my client's experience that I lost a sense of time and space.", cat: 'immersion', reverse: false },
  { q: 'I was fully in the moment during this session.', cat: 'immersion', reverse: false },
  { q: "I felt fully immersed in my client's experience and yet still centered within myself.", cat: 'immersion', reverse: false },
  // Attunement & Connection (5 questions)
  { q: 'I felt a sense of deep appreciation and respect for my client as a person.', cat: 'attunement', reverse: false },
  { q: "I felt alert and attuned to the nuances and subtleties of my client's experience.", cat: 'attunement', reverse: false },
  { q: 'My responses were guided by feelings, words, images, or intuitions that emerged from being with my client.', cat: 'attunement', reverse: false },
  { q: 'I felt in sync with my client in a way that let me sense what they were experiencing.', cat: 'attunement', reverse: false },
  { q: "I felt genuinely interested in my client's experience.", cat: 'attunement', reverse: false },
  // Emotional Availability (4 questions, some reverse-scored)
  { q: 'I was able to set aside my own demands and worries to be with my client.', cat: 'emotional', reverse: false },
  { q: 'I felt distant or disconnected from my client.', cat: 'emotional', reverse: true },
  { q: 'There were moments when my outward response was different from how I actually felt inside.', cat: 'emotional', reverse: true },
  { q: 'I felt a distance or emotional barrier between my client and myself.', cat: 'emotional', reverse: true },
  // Focus & Sustained Attention (7 questions, all reverse-scored)
  { q: 'I felt tired or bored.', cat: 'focus', reverse: true },
  { q: 'I found it difficult to listen to my client.', cat: 'focus', reverse: true },
  { q: 'Time seemed to really drag.', cat: 'focus', reverse: true },
  { q: 'I found it difficult to concentrate.', cat: 'focus', reverse: true },
  { q: 'I felt impatient or critical.', cat: 'focus', reverse: true },
  { q: "I couldn't wait for the session to be over.", cat: 'focus', reverse: true },
  { q: 'My thoughts sometimes drifted away from what was happening in the moment.', cat: 'focus', reverse: true },
];

export const catMeta: Record<string, CategoryMeta> = {
  immersion: { emoji: '🌊', name: 'Immersion & Flow', desc: 'How deeply you entered the shared experience of the session.' },
  attunement: { emoji: '🎯', name: 'Attunement & Connection', desc: "How sensitively you tracked your client's inner world." },
  emotional: { emoji: '💛', name: 'Emotional Availability', desc: 'How open and congruent you were emotionally.' },
  focus: { emoji: '🔍', name: 'Focus & Sustained Attention', desc: 'How steady your attention and concentration remained.' }
};
